import type {EnvironmentContext} from '@wix/document-manager-core'
import type {Pointer} from '@wix/document-services-types'
import type {AnalyzeParams, AnalysisResult} from './types'

import {
    aiGatewayUrl,
    proactiveAiAnalysisPromptId,
    proactiveAiAnalyzeErrorType,
    proactiveAiAnalyzeErrorMessage,
    proactiveAiAnalyzeBadJsonErrorType,
    proactiveAiAnalyzeBadJsonErrorMessage,
    promptHubAppId
} from '../constants'
import {ReportableError} from '@wix/document-manager-utils'

const parseAnalysisResponse = (responseText: string, scope: string, targetPointer: Pointer): AnalysisResult => {
    const jsonMatch = responseText.match(/\{[\s\S]*\}/)
    if (!jsonMatch) {
        throw new ReportableError({
            errorType: proactiveAiAnalyzeBadJsonErrorType,
            message: proactiveAiAnalyzeBadJsonErrorMessage,
            extras: {responseText}
        })
    }

    const parsed = JSON.parse(jsonMatch[0])

    return {
        scope: parsed.scope ?? scope,
        targetPointer,
        signals: {
            textTopics: parsed.signals?.textTopics ?? [],
            headings: parsed.signals?.headings ?? [],
            paragraphs: parsed.signals?.paragraphs ?? [],
            componentCount: parsed.signals?.componentCount,
            usedColors: parsed.signals?.usedColors ?? [],
            usedFonts: parsed.signals?.usedFonts ?? []
        },
        insights: (parsed.insights ?? []).map((insight: any, index: number) => ({
            id: insight.id ?? `ins-${insight.type?.toLowerCase()?.replace(/_/g, '-')}-${index + 1}`,
            type: insight.type,
            confidence: insight.confidence ?? 0,
            impact: insight.impact ?? 'medium',
            scope: insight.scope ?? scope,
            targetPointer,
            details: {
                message: insight.details?.message ?? '',
                expectedKeywords: insight.details?.expectedKeywords,
                foundKeywords: insight.details?.foundKeywords,
                offBrandColors: insight.details?.offBrandColors,
                brandColors: insight.details?.brandColors
            }
        }))
    }
}

export const analyze = async (
    environmentContext: EnvironmentContext,
    params: AnalyzeParams
): Promise<AnalysisResult> => {
    const {serverFacade} = environmentContext
    const {scope, targetPointer, siteContext, text} = params

    const requestData = {
        appDefId: promptHubAppId,
        promptId: proactiveAiAnalysisPromptId,
        params: {
            site_context: JSON.stringify(siteContext),
            text: JSON.stringify(text?.outline ?? {})
        },
        userRequestInfo: {
            additionalTags: 'proactiveAiAnalysis'
        }
    }

    try {
        const response = await serverFacade.post(aiGatewayUrl, requestData)
        const generatedText = response.response.generatedTexts[0]
        return parseAnalysisResponse(generatedText, scope, targetPointer)
    } catch (e: any) {
        if (e.errorType === proactiveAiAnalyzeBadJsonErrorType) {
            throw e
        }
        throw new ReportableError({
            errorType: proactiveAiAnalyzeErrorType,
            message: proactiveAiAnalyzeErrorMessage,
            extras: {
                scope,
                targetPointer,
                originalError: e?.message
            }
        })
    }
}
